import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import CreatableSelect from "react-select/creatable";
import Swal from "sweetalert2";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faAngleLeft,
  faAngleRight,
  faCalendarAlt,
  faCheckSquare,
  faCog,
  faDownload,
  faFileAlt,
  faFileUpload,
  faPaperclip,
  faPrint,
  faSearch,
  faSort,
  faSortDown,
  faSortUp,
  faSyncAlt,
  faTags,
  faTimes,
  faUpload,
} from "@fortawesome/free-solid-svg-icons";
import {
  addSettlementAttachment,
  exportSettlementCheckStatusReport,
  exportSettlementListCsv,
  getSettlementCheckStatus,
  getSettlementBatchPrintCvHtml,
  getSettlementList,
  saveSettlementChequeDetails,
  saveSettlementCheckStatus,
  tagSettlementDateDeducted,
  uploadSettlementBatchDateDeducted,
  uploadSettlementForm2307,
  uploadSettlementProviderOr,
} from "../../services/settlementApi";
import ProviderDeliveryAddressLabelGenerator from "./components/ProviderDeliveryAddressLabelGenerator";
import BatchPrintPisAndForm2307Panel from "./components/BatchPrintPisAndForm2307Panel";

// ─── Helpers ─────────────────────────────────────────────────────────────────

const formatCurrency = (value) => {
  const n = Number(value ?? 0);
  if (Number.isNaN(n)) return value || "";
  return new Intl.NumberFormat("en-PH", {
    style: "currency",
    currency: "PHP",
    minimumFractionDigits: 2,
  }).format(n);
};

const toDisplayDate = (value) => {
  const v = String(value ?? "").trim();
  if (!v) return "";
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(v)) return v;

  const isoDate = v.slice(0, 10);
  if (/^\d{4}-\d{2}-\d{2}$/.test(isoDate)) {
    const [year, month, day] = isoDate.split("-");
    return `${day}/${month}/${year}`;
  }

  return v;
};

const COLUMN_FILTER_KEYS = [
  "status",
  "ppd_term",
  "processed_date",
  "due_date",
  "provider_code",
  "check_no",
  "cv_no",
  "credited_date",
  "payment_ref_no",
];

const DISPLAY_DATE_FILTER_KEYS = new Set(["credited_date"]);

const createEmptyColumnFilters = () =>
  COLUMN_FILTER_KEYS.reduce((acc, key) => ({ ...acc, [key]: [] }), {});

const buildColumnFilterParams = (filters) => {
  const params = {};

  Object.entries(filters).forEach(([key, values]) => {
    const cleanValues = (Array.isArray(values) ? values : [])
      .map((value) => String(value ?? "").trim())
      .filter(Boolean);

    if (cleanValues.length > 0) {
      params[`filter_${key}`] = cleanValues.join("|");
    }
  });

  return params;
};

const getActiveColumnFilterCount = (filters) =>
  Object.values(filters).reduce((count, values) => (
    count + (Array.isArray(values) ? values.length : 0)
  ), 0);

const normalizeColumnFilterOptions = (key, options = []) =>
  (Array.isArray(options) ? options : [])
    .map((option) => {
      const value = String(option?.value ?? "").trim();
      const rawLabel = String(option?.label ?? option?.value ?? "").trim();
      const label = DISPLAY_DATE_FILTER_KEYS.has(key) ? toDisplayDate(rawLabel || value) : rawLabel;

      return {
        value,
        label: label || value,
      };
    })
    .filter((option) => option.value !== "");

const normalizeDateForApi = (value) => {
  const v = String(value ?? "").trim();
  if (!v) return "";
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(v)) return v;

  if (/^\d{4}-\d{2}-\d{2}$/.test(v)) {
    const [year, month, day] = v.split("-");
    return `${day}/${month}/${year}`;
  }

  return v;
};

const isStrictDdMmYyyy = (value) => {
  const v = String(value ?? "").trim();
  const match = v.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (!match) return false;

  const day = Number(match[1]);
  const month = Number(match[2]);
  const year = Number(match[3]);
  const date = new Date(Date.UTC(year, month - 1, day));

  return (
    date.getUTCFullYear() === year &&
    date.getUTCMonth() + 1 === month &&
    date.getUTCDate() === day
  );
};

const SortIcon = ({ col, sortBy, sortDir }) => {
  if (sortBy !== col) return <FontAwesomeIcon icon={faSort} className="ml-1 text-gray-300" />;
  return sortDir === "asc"
    ? <FontAwesomeIcon icon={faSortUp} className="ml-1 text-blue-400" />
    : <FontAwesomeIcon icon={faSortDown} className="ml-1 text-blue-400" />;
};

const SETTLEMENT_TABLE_COLUMNS = [
  { col: "ppd_term", label: "PPD Term", filterKey: "ppd_term" },
  { col: "payment_mode", label: "Payment Mode" },
  { col: "processed_date", label: "Date Processed", filterKey: "processed_date" },
  { col: "due_date", label: "Due Date", filterKey: "due_date" },
  { col: "date_deducted", label: "Actual Date Deducted" },
  { col: "tat", label: "TAT" },
  { col: "provider_name", label: "Provider", filterKey: "provider_code", filterClassName: "min-w-[220px]" },
  { col: "bank_charge", label: "Bank Charge" },
  { col: "net_payable", label: "Net Payable" },
  { col: "check_no", label: "Check Number", filterKey: "check_no" },
  { col: "cv_no", label: "CV#", filterKey: "cv_no" },
  { col: "credited_date", label: "CV Date (formerly Credit Date)", filterKey: "credited_date", filterClassName: "min-w-[190px]" },
  { col: "payment_ref_no", label: "Payment Ref #", filterKey: "payment_ref_no", filterClassName: "min-w-[190px]" },
  { col: "pis_tag", label: "PIS" },
  { col: "pis_no_ppd_file_tag", label: "PIS (No PPD)" },
  { col: "form_2307_tag", label: "Form 2307 (Provider)" },
  { col: "billing_tag", label: "Billing PPD" },
  { col: "deposit_slip_tag", label: "Deposit Slip" },
  { col: "pis_mail_datetime", label: "Mail Sent" },
  { col: "or_tag", label: "Amaphil O.R" },
  { col: "provider_or_tag", label: "Provider O.R" },
  { col: "provider_or_no", label: "Provider OR No." },
  { col: "status", label: "Status", filterKey: "status" },
  { col: "ppd", label: "PPD" },
];

const columnFilterSelectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: "30px",
    borderColor: state.isFocused ? "#60a5fa" : "#d1d5db",
    boxShadow: state.isFocused ? "0 0 0 1px #60a5fa" : "none",
    fontSize: "12px",
    "&:hover": { borderColor: "#93c5fd" },
  }),
  valueContainer: (base) => ({
    ...base,
    maxHeight: "58px",
    overflowY: "auto",
    padding: "1px 4px",
  }),
  indicatorsContainer: (base) => ({
    ...base,
    minHeight: "28px",
  }),
  dropdownIndicator: (base) => ({
    ...base,
    padding: "3px",
  }),
  clearIndicator: (base) => ({
    ...base,
    padding: "3px",
  }),
  multiValue: (base) => ({
    ...base,
    margin: "1px",
    backgroundColor: "#e0f2fe",
  }),
  multiValueLabel: (base) => ({
    ...base,
    color: "#075985",
    fontSize: "11px",
    padding: "1px 3px",
  }),
  multiValueRemove: (base) => ({
    ...base,
    color: "#0369a1",
    ":hover": {
      backgroundColor: "#bae6fd",
      color: "#075985",
    },
  }),
  menu: (base) => ({
    ...base,
    fontSize: "12px",
  }),
  menuPortal: (base) => ({
    ...base,
    zIndex: 9999,
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected ? "#0ea5e9" : state.isFocused ? "#e0f2fe" : "#ffffff",
    color: state.isSelected ? "#ffffff" : "#111827",
  }),
};

const PpdTermBadge = ({ value }) => {
  switch (value) {
    case "ot": return <span className="px-2 py-0.5 rounded text-xs font-semibold bg-blue-100 text-blue-700">Outright</span>;
    case "rt": return <span className="px-2 py-0.5 rounded text-xs font-semibold bg-green-100 text-green-700">Rebate</span>;
    default:   return <span className="px-2 py-0.5 rounded text-xs font-semibold bg-yellow-100 text-yellow-700">NA</span>;
  }
};

const StatusBadge = ({ value }) => {
  switch (value) {
    case "Paid":       return <span className="px-2 py-0.5 rounded text-xs font-semibold bg-green-100 text-green-700">Paid</span>;
    case "Incomplete": return <span className="px-2 py-0.5 rounded text-xs font-semibold bg-yellow-100 text-yellow-700">Incomplete</span>;
    default:           return <span className="px-2 py-0.5 rounded text-xs font-semibold bg-red-100 text-red-700">Unpaid</span>;
  }
};

const CHECK_STATUS_OPTIONS = [
  "For Clearing",
  "Cleared",
  "Outstanding",
  "Stale",
  "Replaced",
  "Cancelled",
];

const REPLACEMENT_REASON_OPTIONS = [
  "Staled",
  "Wrong Payee Name",
  "Ripped Check",
  "Should be Paid to Hospital/Clinic",
  "Unreadable Due to Print",
  "Wrong Tagged Provider",
];

const REPLACEMENT_FEE_REASONS = ["Staled", "Ripped Check"];
const CHECK_STATUS_FLASH_TOAST_KEY = "settlement_check_status_flash_toast";

const LEGACY_ALLACCESS_BASE_URL = "https://allaccess.net.ph";
const ALLACCESS_HOST_PATTERN = /^allaccess\.(?:amaphil\.com\.ph|net\.ph)$/i;

const isAbsoluteUrl = (value) => /^https?:\/\//i.test(String(value ?? "").trim());

const getFileNameFromPath = (value) => {
  const pathOnly = String(value ?? "").trim().replace(/\\/g, "/").split(/[?#]/)[0];
  const fileName = pathOnly.split("/").filter(Boolean).pop() || "";

  try {
    return decodeURIComponent(fileName);
  } catch {
    return fileName;
  }
};

const getAllAccessUploadOrStorageFileName = (value) => {
  try {
    const url = new URL(String(value ?? "").trim());
    if (!ALLACCESS_HOST_PATTERN.test(url.hostname)) return null;
    if (!/^\/(?:upload_doc|storage)\//i.test(url.pathname)) return null;

    return getFileNameFromPath(url.pathname);
  } catch {
    return null;
  }
};

const buildFileUrl = (baseUrl, folder, fileName) => {
  const cleanFileName = String(fileName ?? "").trim();
  if (!cleanFileName) return null;
  if (isAbsoluteUrl(cleanFileName)) return cleanFileName;

  return `${String(baseUrl).replace(/\/$/, "")}/${String(folder).replace(/^\/|\/$/g, "")}/${encodeURIComponent(cleanFileName)}`;
};

const buildLegacyDownloadUrl = (folder, fileName) => {
  const cleanFileName = String(fileName ?? "").trim();
  if (!cleanFileName) return null;
  if (isAbsoluteUrl(cleanFileName)) {
    const knownAllAccessFileName = getAllAccessUploadOrStorageFileName(cleanFileName);
    return knownAllAccessFileName
      ? buildFileUrl(LEGACY_ALLACCESS_BASE_URL, `upload_doc/${folder}`, knownAllAccessFileName)
      : cleanFileName;
  }

  return buildFileUrl(LEGACY_ALLACCESS_BASE_URL, `upload_doc/${folder}`, cleanFileName);
};

const buildLegacyDepositSlipDownloadUrl = (fileName) => {
  const cleanFileName = String(fileName ?? "").trim();
  if (!cleanFileName) return null;

  if (cleanFileName.includes("_|")) {
    const [refNo = "", receivedDate = "", receivedBy = "", status = ""] = cleanFileName.split("_|");
    if (!refNo || !receivedDate) return null;

    const params = new URLSearchParams({
      received_date: receivedDate,
      ref_no: refNo,
      received_by: receivedBy,
      status,
    });

    return `${LEGACY_ALLACCESS_BASE_URL}/ajax/test/deposit-slip/?${params.toString()}`;
  }

  return buildLegacyDownloadUrl("settlement_attachments", cleanFileName);
};

const buildS3PublicShareDownloadUrl = (value) => {
  const cleanUrl = String(value ?? "").trim();
  return isAbsoluteUrl(cleanUrl) ? cleanUrl : null;
};

const addDaysToIsoDate = (value, days) => {
  const dateValue = String(value ?? "").slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(dateValue)) return "";

  const [year, month, day] = dateValue.split("-").map(Number);
  const date = new Date(Date.UTC(year, month - 1, day));
  if (Number.isNaN(date.getTime())) return "";

  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
};

const getApiErrorMessage = (err, fallback) => {
  const message = err?.response?.data?.message ?? err?.response?.data?.error ?? err?.message;

  if (typeof message === "string" && message.trim()) {
    return message;
  }

  if (message && typeof message === "object") {
    for (const value of Object.values(message)) {
      if (Array.isArray(value) && value.length > 0) {
        return String(value[0]);
      }
      if (typeof value === "string" && value.trim()) {
        return value;
      }
    }
  }

  return fallback;
};

const waitForPrintWindowImages = (printWindow, timeoutMs = 4000) =>
  new Promise((resolve) => {
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      resolve();
    };

    const timeoutId = setTimeout(finish, timeoutMs);
    const images = Array.from(printWindow.document.images || []);

    if (images.length === 0) {
      clearTimeout(timeoutId);
      finish();
      return;
    }

    let loaded = 0;
    const checkLoaded = () => {
      loaded += 1;
      if (loaded >= images.length) {
        clearTimeout(timeoutId);
        finish();
      }
    };

    images.forEach((image) => {
      if (image.complete) {
        checkLoaded();
      } else {
        image.addEventListener("load", checkLoaded, { once: true });
        image.addEventListener("error", checkLoaded, { once: true });
      }
    });
  });

const EMPTY_CHEQUE = {
  tbl_id: "", provider_code: "", check_no: "", check_amount: "",
  credited_date: "", provider_or_no: "", provider_or_date: "", provider_or_amount: "",
  payee_name: "", payment_ref_no: "",
};

const EMPTY_CHECK_STATUS = {
  sr_tbl_id: "",
  payment_ref_no: "",
  provider_code: "",
  provider_name: "",
  cv_no: "",
  check_no: "",
  check_date: "",
  computed_stale_date: "",
  check_status: "For Clearing",
  date_cleared: "",
  replacement_check_indicator: "0",
  original_check_number: "",
  new_check_number: "",
  new_check_date: "",
  reason_for_replacement: "",
  replacement_fee_deduction_amount: "0.00",
  number_of_times_replaced: "0",
  needs_replacement_letter: 0,
  remarks: "",
};

const EMPTY_ATTACH = {
  tbl_id: "",
  type: "deposit_slip",
  amap_or_no: "",
  amap_or_date: "",
  amap_or_amount: "",
  file: null,
  no_attachment: false,
  has_existing: false,
  payment_ref_no: "",
  provider_name: "",
  check_no: "",
  credited_date: "",
};

// ─── Component ───────────────────────────────────────────────────────────────

const SettlementListPage = () => {
  const navigate = useNavigate();

  // ── Table state ──
  const [rows, setRows]           = useState([]);
  const [loading, setLoading]     = useState(false);
  const [search, setSearch]       = useState("");
  const [appliedSearch, setApplied] = useState("");
  const [searchBy, setSearchBy]   = useState("all");
  const [sortBy, setSortBy]       = useState("processed_date");
  const [sortDir, setSortDir]     = useState("desc");
  const [page, setPage]           = useState(1);
  const [perPage, setPerPage]     = useState(10);
  const [pagination, setPagination] = useState({});
  const [columnFilters, setColumnFilters] = useState(() => createEmptyColumnFilters());
  const [filterOptions, setFilterOptions] = useState({});

  // ── Selection for batch CV ──
  const [selectedTblIds, setSelectedTblIds] = useState([]);

  // ── Modal visibility ──
  const [chequeModal, setChequeModal]       = useState(false);
  const [attachModal, setAttachModal]       = useState(false);
  const [form2307Modal, setForm2307Modal]   = useState(false);
  const [provOrModal, setProvOrModal]       = useState(false);
  const [deductedModal, setDeductedModal]   = useState(false);
  const [checkStatusModal, setCheckStatusModal] = useState(false);
  const [checkStatusReportModal, setCheckStatusReportModal] = useState(false);
  const [batchPrintModal, setBatchPrintModal] = useState(false);
  const [deliveryLabelModal, setDeliveryLabelModal] = useState(false);
  const [batchDeductedModal, setBatchDeductedModal] = useState(false);

  // ── Modal data ──
  const [chequeData, setChequeData]   = useState(EMPTY_CHEQUE);
  const [attachData, setAttachData]   = useState(EMPTY_ATTACH);
  const [form2307Row, setForm2307Row] = useState(null);
  const [provOrRow, setProvOrRow]     = useState(null);
  const [deductedRow, setDeductedRow] = useState(null);
  const [checkStatusRow, setCheckStatusRow] = useState(null);
  const [checkStatusData, setCheckStatusData] = useState(EMPTY_CHECK_STATUS);
  const [checkStatusReportFilters, setCheckStatusReportFilters] = useState({
    date_from: "",
    date_to: "",
    new_date_from: "",
    new_date_to: "",
  });
  const [dateDeducted, setDateDeducted] = useState("");
  const [deliveryLabelSnapshot, setDeliveryLabelSnapshot] = useState({
    providerCodes: "",
    rows: [],
    status: "",
    fromContactNumber: "",
  });

  // ── Upload files ──
  const [batchDeductedFile, setBatchDeductedFile] = useState(null);
  const [form2307File, setForm2307File]       = useState(null);
  const [provOrFile, setProvOrFile]           = useState(null);

  // ── Submitting guards ──
  const [submitting, setSubmitting] = useState(false);

  // ─── Fetch data ─────────────────────────────────────────────────────────────

  useEffect(() => {
    const flashMessage = sessionStorage.getItem(CHECK_STATUS_FLASH_TOAST_KEY);
    if (!flashMessage) return;

    sessionStorage.removeItem(CHECK_STATUS_FLASH_TOAST_KEY);
    setTimeout(() => {
      toast.success(flashMessage);
    }, 250);
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getSettlementList({
        search: appliedSearch,
        search_by: searchBy,
        sort_by: sortBy,
        sort_direction: sortDir,
        page,
        per_page: perPage,
        include_filter_options: 1,
        ...buildColumnFilterParams(columnFilters),
      });
      setRows(res.data.data ?? []);
      setPagination(res.data.pagination ?? {});
      setFilterOptions(res.data.filter_options ?? {});
    } catch {
      toast.error("Failed to load settlement records.");
    } finally {
      setLoading(false);
    }
  }, [appliedSearch, searchBy, sortBy, sortDir, page, perPage, columnFilters]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // ─── Sort ────────────────────────────────────────────────────────────────────

  const handleSort = (col) => {
    if (sortBy === col) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(col);
      setSortDir("desc");
    }
    setPage(1);
  };

  // ─── Search ──────────────────────────────────────────────────────────────────

  const handleSearch = () => { setApplied(search); setPage(1); };
  const handleSearchKeyDown = (e) => { if (e.key === "Enter") handleSearch(); };
  const handleColumnFilterChange = (filterKey, values) => {
    setColumnFilters((prev) => ({
      ...prev,
      [filterKey]: values,
    }));
    setPage(1);
  };
  const clearColumnFilters = () => {
    setColumnFilters(createEmptyColumnFilters());
    setPage(1);
  };

  // ─── CSV Export ──────────────────────────────────────────────────────────────

  const handleExportCsv = async () => {
    try {
      const res = await exportSettlementListCsv({
        search: appliedSearch, search_by: searchBy,
        sort_by: sortBy, sort_direction: sortDir,
        ...buildColumnFilterParams(columnFilters),
      });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a   = document.createElement("a");
      a.href = url;
      a.download = `settlement_list_${Date.now()}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("CSV export failed.");
    }
  };

  // ─── Cheque Details Modal ─────────────────────────────────────────────────────

  const handleExportCheckStatusReport = async () => {
    setSubmitting(true);
    try {
      const res = await exportSettlementCheckStatusReport(checkStatusReportFilters);
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `check_status_report_${Date.now()}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Check Status Report CSV generated.");
      setCheckStatusReportModal(false);
    } catch (err) {
      toast.error(getApiErrorMessage(err, "Failed to generate Check Status Report."));
    } finally {
      setSubmitting(false);
    }
  };

  const openChequeModal = (row) => {
    setChequeData({
      tbl_id:              row.tbl_id,
      provider_code:       row.provider_code,
      payee_name:          row.payee_name || "",
      payment_ref_no:      row.payment_ref_no || "",
      check_no:            row.check_no || "",
      check_amount:        row.check_amount || "",
      credited_date:       toDisplayDate(row.credited_date),
      provider_or_no:      row.provider_or_no || "",
      provider_or_date:    row.provider_or_date || "",
      provider_or_amount:  row.provider_or_amount || "",
    });
    setChequeModal(true);
  };

  const handleSaveCheque = async () => {
    if (!chequeData.check_no) {
      toast.warn("Check number is required.");
      return;
    }

    const normalizedCreditedDate = normalizeDateForApi(chequeData.credited_date);
    if (normalizedCreditedDate && !isStrictDdMmYyyy(normalizedCreditedDate)) {
      toast.warn("CV Date must be in DD/MM/YYYY format.");
      return;
    }

    setSubmitting(true);
    try {
      await saveSettlementChequeDetails({
        ...chequeData,
        credited_date: normalizedCreditedDate,
      });
      toast.success("Cheque details saved.");
      setChequeModal(false);
      fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.message ?? "Failed to save cheque details.");
    } finally {
      setSubmitting(false);
    }
  };

  // ─── Attach Files Modal (deposit_slip / or_amap / billing_ppd) ───────────────

  const openAttachModal = (row, type) => {
    const hasExisting = type === "deposit_slip"
      ? Number(row.deposit_slip_tag || 0) === 1
        : type === "or_amap"
        ? Number(row.or_tag || 0) === 1
        : type === "billing_ppd"
          ? !!(row.billing_ppd_public_share_url || row.billing_ppd_public_share_token)
          : false;

    setAttachData({
      tbl_id: row.tbl_id,
      type,
      amap_or_no: row.amap_or_no || "",
      amap_or_date: row.amap_or_date || "",
      amap_or_amount: row.amap_or_amount || "",
      file: null,
      no_attachment: false,
      has_existing: hasExisting,
      payment_ref_no: row.payment_ref_no || "",
      provider_name: row.provider_name || "",
      check_no: row.check_no || "",
      credited_date: toDisplayDate(row.credited_date),
    });
    setAttachModal(true);
  };

  const handleSaveAttachment = async () => {
    const isDepositSlip = attachData.type === "deposit_slip";
    const isAmapOr = attachData.type === "or_amap";
    const isBillingPpd = attachData.type === "billing_ppd";
    const allowsNoAttachment = isDepositSlip || isBillingPpd;
    const requiresAmapOrFields = isAmapOr || isBillingPpd;
    const hasExisting = Boolean(attachData.has_existing);

    if (allowsNoAttachment) {
      if (!attachData.file && !attachData.no_attachment) {
        toast.warn("Please select a file or choose No Attachment.");
        return;
      }
    } else if (!attachData.file) {
      toast.warn("Please select a file.");
      return;
    }

    if (requiresAmapOrFields) {
      if (!attachData.amap_or_no || !attachData.amap_or_date || !String(attachData.amap_or_amount ?? "").trim()) {
        toast.warn("Amaphil OR No., Date, and Amount are required.");
        return;
      }
    }

    if (isDepositSlip) {
      const depositSlipConfirm = await Swal.fire({
        title: hasExisting ? "Replace Deposit Slip and Notify Recipient?" : "Upload Deposit Slip and Notify Recipient?",
        text: attachData.no_attachment
          ? "This will mark the Deposit Slip as No Attachment. Email will only be sent when a deposit slip file/link is available and the settlement is Paid."
          : "After a successful upload, the system will automatically send or resend the settlement email to the Provider or Physician once the settlement is Paid.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes, proceed",
        cancelButtonText: "Cancel",
      });

      if (!depositSlipConfirm.isConfirmed) return;
    }

    if (hasExisting && (isAmapOr || isBillingPpd)) {
      const replaceTitle = isBillingPpd
          ? "Replace existing Billing PPD attachment?"
          : "Replace existing Amaphil O.R?";
      const replaceText = isBillingPpd
          ? (
            attachData.no_attachment
              ? "This will proceed Billing PPD submission with No Attachment."
              : "Uploading a new file will replace the currently uploaded Billing PPD attachment."
          )
          : "Uploading a new file will replace the currently uploaded Amaphil O.R.";
      const confirmReplace = await Swal.fire({
        title: replaceTitle,
        text: replaceText,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes, proceed",
        cancelButtonText: "Cancel",
      });

      if (!confirmReplace.isConfirmed) return;
    }

    const fd = new FormData();
    fd.append("tbl_id", attachData.tbl_id);
    fd.append("type",   attachData.type);
    if (attachData.file) {
      fd.append("file", attachData.file);
    }
    if (allowsNoAttachment) {
      fd.append("no_attachment", attachData.no_attachment ? "1" : "0");
    }
    if (requiresAmapOrFields) {
      fd.append("amap_or_no",     attachData.amap_or_no);
      fd.append("amap_or_date",   attachData.amap_or_date);
      fd.append("amap_or_amount", attachData.amap_or_amount);
    }
    setSubmitting(true);
    try {
      const res = await addSettlementAttachment(fd);
      const replaced = Boolean(res?.data?.replaced_existing || hasExisting);
      if (isDepositSlip && attachData.no_attachment && !attachData.file) {
        toast.success(replaced ? "Deposit Slip replaced with No Attachment." : "Deposit Slip marked as No Attachment.");
      } else if (isBillingPpd && attachData.no_attachment && !attachData.file) {
        toast.success("Billing PPD submitted with No Attachment.");
      } else if (isBillingPpd) {
        toast.success(replaced ? "Billing PPD attachment replaced successfully." : "Billing PPD attachment uploaded.");
      } else if (isAmapOr) {
        toast.success(replaced ? "Amaphil O.R replaced successfully." : "Amaphil O.R uploaded.");
      } else {
        toast.success(replaced ? "Attachment replaced successfully." : "Attachment saved.");
      }
      setAttachModal(false);
      fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.message ?? "Failed to save attachment.");
    } finally {
      setSubmitting(false);
    }
  };

  // ─── Form 2307 Modal ─────────────────────────────────────────────────────────

  const openForm2307Modal = (row) => { setForm2307Row(row); setForm2307File(null); setForm2307Modal(true); };

  const handleSaveForm2307 = async () => {
    if (!form2307Row?.tbl_id) { toast.error("Settlement row is missing."); return; }
    if (!form2307File) { toast.warn("Please select a file."); return; }

    const hasExistingForm2307 = Number(form2307Row?.form_2307_tag || 0) === 1
      && !!(form2307Row?.form_2307_file || form2307Row?.form_2307_public_share_url);

    if (hasExistingForm2307) {
      const confirmReplace = await Swal.fire({
        title: "Replace existing Form 2307?",
        text: "Uploading a new file will replace the currently uploaded Form 2307.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes, replace",
        cancelButtonText: "Cancel",
      });

      if (!confirmReplace.isConfirmed) return;
    }

    const fd = new FormData();
    fd.append("tbl_id", form2307Row.tbl_id);
    fd.append("file",   form2307File);
    setSubmitting(true);
    try {
      const res = await uploadSettlementForm2307(fd);
      const replaced = Boolean(res?.data?.replaced_existing || hasExistingForm2307);
      toast.success(replaced ? "Form 2307 replaced successfully." : "Form 2307 uploaded.");
      setForm2307Modal(false);
      fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.message ?? "Failed to upload Form 2307.");
    } finally {
      setSubmitting(false);
    }
  };

  // ─── Provider OR Modal ───────────────────────────────────────────────────────

  const openProvOrModal = (row) => { setProvOrRow(row); setProvOrFile(null); setProvOrModal(true); };

  const handleSaveProvOr = async () => {
    if (!provOrRow?.tbl_id) { toast.error("Settlement row is missing."); return; }
    if (!provOrFile) { toast.warn("Please select a file."); return; }

    const hasExistingProvOr = Number(provOrRow?.provider_or_tag || 0) === 1
      && !!(provOrRow?.provider_or_file || provOrRow?.provider_or_public_share_url);

    if (hasExistingProvOr) {
      const confirmReplace = await Swal.fire({
        title: "Replace existing Provider OR?",
        text: "Uploading a new file will replace the currently uploaded Provider OR.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes, replace",
        cancelButtonText: "Cancel",
      });

      if (!confirmReplace.isConfirmed) return;
    }

    const fd = new FormData();
    fd.append("tbl_id", provOrRow.tbl_id);
    fd.append("file",   provOrFile);
    setSubmitting(true);
    try {
      const res = await uploadSettlementProviderOr(fd);
      const replaced = Boolean(res?.data?.replaced_existing || hasExistingProvOr);
      toast.success(replaced ? "Provider OR replaced successfully." : "Provider OR uploaded.");
      setProvOrModal(false);
      fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.message ?? "Failed to upload Provider OR.");
    } finally {
      setSubmitting(false);
    }
  };

  // ─── Tag Date Deducted Modal ──────────────────────────────────────────────────

  const openDeductedModal = (row) => { setDeductedRow(row); setDateDeducted(""); setDeductedModal(true); };

  const handleTagDateDeducted = async () => {
    if (!dateDeducted) { toast.warn("Please select a date."); return; }
    setSubmitting(true);
    try {
      const res = await tagSettlementDateDeducted({
        payment_ref_no: deductedRow.payment_ref_no,
        date_deducted:  dateDeducted,
      });
      if (res.data.result === "has_date_deducted") {
        toast.warn(`Already tagged: ${res.data.cur_date_deducted}`);
      } else {
        toast.success("Date deducted tagged.");
        setDeductedModal(false);
        fetchData();
      }
    } catch (err) {
      toast.error(err?.response?.data?.message ?? "Failed to tag date deducted.");
    } finally {
      setSubmitting(false);
    }
  };

  // ─── Batch Upload handlers ────────────────────────────────────────────────────

  // ─── Provider Delivery Address Label Generation ─────────────────────────────

  const buildCheckStatusData = (row, saved = {}) => {
    const checkDateValue = String(saved.check_date || row?.credited_date || "").trim();
    const checkDate = checkDateValue === "0000-00-00" ? "" : checkDateValue.slice(0, 10);
    const replacementIndicator = Number(saved.replacement_check_indicator ?? 0) === 1 ? "1" : "0";

    return {
      ...EMPTY_CHECK_STATUS,
      sr_tbl_id: saved.sr_tbl_id || row?.tbl_id || "",
      payment_ref_no: saved.payment_ref_no || row?.payment_ref_no || "",
      provider_code: saved.provider_code || row?.provider_code || "",
      provider_name: saved.provider_name || row?.provider_name || "",
      cv_no: saved.cv_no || row?.cv_no || "",
      check_no: saved.check_no || row?.check_no || "",
      check_date: /^\d{4}-\d{2}-\d{2}$/.test(checkDate) ? checkDate : "",
      computed_stale_date: saved.computed_stale_date || addDaysToIsoDate(checkDate, 180),
      check_status: saved.check_status || "For Clearing",
      date_cleared: saved.date_cleared || "",
      replacement_check_indicator: replacementIndicator,
      original_check_number: saved.original_check_number || "",
      new_check_number: saved.new_check_number || "",
      new_check_date: saved.new_check_date || "",
      reason_for_replacement: saved.reason_for_replacement || "",
      replacement_fee_deduction_amount: String(saved.replacement_fee_deduction_amount ?? "0.00"),
      number_of_times_replaced: String(saved.number_of_times_replaced ?? "0"),
      needs_replacement_letter: Number(saved.needs_replacement_letter || 0),
      remarks: saved.remarks || "",
    };
  };

  const openCheckStatusModal = async (row) => {
    setCheckStatusRow(row);
    setCheckStatusData(buildCheckStatusData(row));
    setCheckStatusModal(true);

    try {
      const res = await getSettlementCheckStatus({
        tbl_id: row.tbl_id,
        cv_no: row.cv_no,
        check_no: row.check_no,
        check_date: row.credited_date,
      });
      setCheckStatusData(buildCheckStatusData(row, res?.data?.data || {}));
    } catch (err) {
      toast.error(getApiErrorMessage(err, "Failed to load check status details."));
    }
  };

  const updateCheckStatusData = (field, value) => {
    setCheckStatusData((prev) => {
      const next = { ...prev, [field]: value };

      if (field === "check_date") {
        next.computed_stale_date = addDaysToIsoDate(value, 180);
      }

      if (field === "check_status" && value !== "Cleared") {
        next.date_cleared = "";
      }

      if (field === "replacement_check_indicator") {
        if (value === "1") {
          next.original_check_number = next.original_check_number || next.check_no;
        } else {
          next.original_check_number = "";
          next.new_check_number = "";
          next.new_check_date = "";
          next.reason_for_replacement = "";
          next.replacement_fee_deduction_amount = "0.00";
          next.number_of_times_replaced = "0";
          next.needs_replacement_letter = 0;
        }
      }

      if (field === "reason_for_replacement" && !REPLACEMENT_FEE_REASONS.includes(value)) {
        next.replacement_fee_deduction_amount = "0.00";
      }

      return next;
    });
  };

  const handleSaveCheckStatus = async () => {
    const isReplacement = checkStatusData.replacement_check_indicator === "1";
    const replacementFee = Number(String(checkStatusData.replacement_fee_deduction_amount || "0").replace(/,/g, ""));

    if (!checkStatusData.cv_no || !checkStatusData.check_no || !checkStatusData.check_status || !checkStatusData.check_date) {
      toast.warn("CV No., Check Number, Check Date, and Check Status are required.");
      return;
    }

    if (checkStatusData.check_status === "Cleared" && !checkStatusData.date_cleared) {
      toast.warn("Date Cleared is required when status is Cleared.");
      return;
    }

    if (isReplacement) {
      if (
        !checkStatusData.original_check_number ||
        !checkStatusData.new_check_number ||
        !checkStatusData.new_check_date ||
        !checkStatusData.reason_for_replacement
      ) {
        toast.warn("Original Check Number, New Check Number, New Check Date, and Reason for Replacement are required.");
        return;
      }

      if (checkStatusData.original_check_number === checkStatusData.new_check_number) {
        toast.warn("New Check Number must be different from Original Check Number.");
        return;
      }

      if (!Number.isNaN(replacementFee) && replacementFee < 0) {
        toast.warn("Replacement Fee Deduction Amount cannot be negative.");
        return;
      }
    }

    const confirmSave = await Swal.fire({
      title: "Save Check Status",
      text: "Are you sure you want to save this check status?",
      icon: "info",
      showCancelButton: true,
      confirmButtonText: "Yes",
      cancelButtonText: "Cancel",
    });

    if (!confirmSave.isConfirmed) return;

    setSubmitting(true);
    try {
      const res = await saveSettlementCheckStatus({
        ...checkStatusData,
        sr_tbl_id: checkStatusRow?.tbl_id || checkStatusData.sr_tbl_id,
        payment_ref_no: String(checkStatusData.payment_ref_no ?? "").trim(),
        provider_code: String(checkStatusData.provider_code ?? "").trim(),
        provider_name: String(checkStatusData.provider_name ?? "").trim(),
        cv_no: String(checkStatusData.cv_no ?? "").trim(),
        check_no: String(checkStatusData.check_no ?? "").trim(),
        original_check_number: String(checkStatusData.original_check_number ?? "").trim(),
        new_check_number: String(checkStatusData.new_check_number ?? "").trim(),
        reason_for_replacement: String(checkStatusData.reason_for_replacement ?? "").trim(),
        remarks: String(checkStatusData.remarks ?? "").trim(),
        replacement_check_indicator: isReplacement ? 1 : 0,
      });

      setCheckStatusData((prev) => ({
        ...prev,
        computed_stale_date: res?.data?.computed_stale_date || prev.computed_stale_date,
        number_of_times_replaced: String(res?.data?.number_of_times_replaced ?? prev.number_of_times_replaced),
        needs_replacement_letter: Number(res?.data?.needs_replacement_letter ?? prev.needs_replacement_letter),
      }));
      const successMessage = res?.data?.message || "Check status has been saved successfully.";
      sessionStorage.setItem(CHECK_STATUS_FLASH_TOAST_KEY, successMessage);
      toast.success(successMessage);
      setTimeout(() => {
        sessionStorage.removeItem(CHECK_STATUS_FLASH_TOAST_KEY);
      }, 8000);
      setCheckStatusModal(false);
      await fetchData();

      if (Number(res?.data?.needs_replacement_letter || 0) === 1) {
        await Swal.fire(
          "Check Replacement Letter Required",
          "Check status has been saved. This check has been replaced more than once.",
          "warning"
        );
      }
    } catch (err) {
      toast.error(getApiErrorMessage(err, "Failed to save check status."));
    } finally {
      setSubmitting(false);
    }
  };

  const handleOpenDeliveryLabelFullPage = () => {
    navigate("/provider-delivery-address-label", {
      state: {
        providerCodes: deliveryLabelSnapshot.providerCodes,
        rows: deliveryLabelSnapshot.rows,
        status: deliveryLabelSnapshot.status,
        fromContactNumber: deliveryLabelSnapshot.fromContactNumber,
      },
    });
    setDeliveryLabelModal(false);
  };

  const handleOpenBatchPrintFullPage = () => {
    setBatchPrintModal(false);
    navigate("/settlement-batch-print-pis-form2307");
  };

  const handleViewCheckVoucher = async (row) => {
    const cvNo = String(row?.cv_no ?? "").trim();

    if (!cvNo || !/^\d+$/.test(cvNo) || Number(cvNo) <= 0) {
      toast.warn("CV number is required before viewing the Check Voucher.");
      return;
    }

    const printWindow = window.open("", "_blank");
    if (!printWindow) {
      toast.error("Unable to open check voucher preview. Please allow pop-ups for this site.");
      return;
    }

    printWindow.document.open();
    printWindow.document.write(`
      <!DOCTYPE html>
      <html lang="en">
        <head>
          <meta charset="UTF-8">
          <title>Check Voucher ${cvNo}</title>
          <style>
            body {
              margin: 0;
              font-family: Arial, sans-serif;
              color: #333;
            }
            .loading {
              align-items: center;
              display: flex;
              height: 100vh;
              justify-content: center;
            }
          </style>
        </head>
        <body><div class="loading">Preparing Check Voucher...</div></body>
      </html>
    `);
    printWindow.document.close();

    setSubmitting(true);
    try {
      const res = await getSettlementBatchPrintCvHtml(cvNo);
      const html = String(res?.data?.html ?? "").trim();
      const cvCount = Number(res?.data?.cv_count ?? 0);

      if (!html || cvCount === 0) {
        printWindow.close();
        toast.info("No check voucher data found for this CV number.");
        return;
      }

      printWindow.document.open();
      printWindow.document.write(html);
      printWindow.document.close();

      await waitForPrintWindowImages(printWindow);

      try {
        printWindow.focus();
        printWindow.print();
      } catch {
        // Keep preview window open even if browser blocks direct print trigger.
      }

      toast.success(`Prepared Check Voucher CV #${cvNo} for printing.`);
    } catch (err) {
      try {
        printWindow.close();
      } catch {
        // Ignore close failures.
      }

      const message =
        (typeof err?.response?.data?.message === "string" && err.response.data.message) ||
        err?.message ||
        "Failed to generate Check Voucher.";
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleGenerateSelectedCheckVouchers = async () => {
    const tblIds = Array.from(
      new Set(
        selectedTblIds
          .map((id) => String(id ?? "").trim())
          .filter((id) => /^\d+$/.test(id) && Number(id) > 0)
      )
    );

    if (tblIds.length === 0) {
      toast.warn("Please select at least one settlement row for Batch CV.");
      return;
    }

    const printWindow = window.open("", "_blank");
    if (!printWindow) {
      toast.error("Unable to open check voucher preview. Please allow pop-ups for this site.");
      return;
    }

    printWindow.document.open();
    printWindow.document.write(`
      <!DOCTYPE html>
      <html lang="en">
        <head>
          <meta charset="UTF-8">
          <title>Batch Check Voucher</title>
          <style>
            body {
              margin: 0;
              font-family: Arial, sans-serif;
              color: #333;
            }
            .loading {
              align-items: center;
              display: flex;
              height: 100vh;
              justify-content: center;
            }
          </style>
        </head>
        <body><div class="loading">Preparing selected Check Voucher...</div></body>
      </html>
    `);
    printWindow.document.close();

    setSubmitting(true);
    try {
      const res = await getSettlementBatchPrintCvHtml("", tblIds);
      const html = String(res?.data?.html ?? "").trim();
      const cvCount = Number(res?.data?.cv_count ?? 0);

      if (!html || cvCount === 0) {
        printWindow.close();
        toast.info("No check voucher data found for the selected rows.");
        return;
      }

      printWindow.document.open();
      printWindow.document.write(html);
      printWindow.document.close();

      await waitForPrintWindowImages(printWindow);

      try {
        printWindow.focus();
        printWindow.print();
      } catch {
        // Keep preview window open even if browser blocks direct print trigger.
      }

      const voucherLabel = cvCount === 1 ? "Check Voucher" : "Check Vouchers";
      const rowLabel = tblIds.length === 1 ? "row" : "rows";
      toast.success(`Prepared ${cvCount} ${voucherLabel} from ${tblIds.length} selected ${rowLabel}.`);
    } catch (err) {
      try {
        printWindow.close();
      } catch {
        // Ignore close failures.
      }

      toast.error(getApiErrorMessage(err, "Failed to generate selected Check Voucher."));
    } finally {
      setSubmitting(false);
    }
  };

  const handleUploadBatchDateDeducted = async () => {
    if (!batchDeductedFile) { toast.warn("Please select a CSV file."); return; }
    const fd = new FormData(); fd.append("file", batchDeductedFile);
    setSubmitting(true);
    try {
      await uploadSettlementBatchDateDeducted(fd);
      toast.success("Batch Date Deducted CSV uploaded.");
      setBatchDeductedModal(false); setBatchDeductedFile(null);
    } catch (err) {
      toast.error(err?.response?.data?.message ?? "Upload failed.");
    } finally {
      setSubmitting(false);
    }
  };

  // ─── Select for batch CV ──────────────────────────────────────────────────────

  const toggleSelect = (tblId) => {
    setSelectedTblIds((prev) =>
      prev.includes(tblId) ? prev.filter((id) => id !== tblId) : [...prev, tblId]
    );
  };
  const selectAll = () => setSelectedTblIds(Array.from(new Set(
    rows
      .map((r) => r.tbl_id)
      .filter((id) => id !== null && id !== undefined && String(id).trim() !== "")
  )));
  const clearAll  = () => setSelectedTblIds([]);

  // ─── File tag helpers ─────────────────────────────────────────────────────────

  const fileTagButton = (file, path, onClickAttach) => {
    if (file) {
      return (
        <a href={file} target="_blank" rel="noreferrer" download
          className="inline-flex items-center gap-1 px-2 py-0.5 text-xs bg-green-100 text-green-700 rounded hover:bg-green-200">
          <FontAwesomeIcon icon={faDownload} /> Attached
        </a>
      );
    }
    return (
      <button onClick={onClickAttach}
        className="inline-flex items-center gap-1 px-2 py-0.5 text-xs bg-gray-100 text-gray-600 rounded hover:bg-gray-200">
        <FontAwesomeIcon icon={faPaperclip} /> Attach
      </button>
    );
  };

  // ─── Render ───────────────────────────────────────────────────────────────────

  const totalPages = pagination.last_page ?? 1;
  const activeColumnFilterCount = getActiveColumnFilterCount(columnFilters);

  return (
    <div className="p-4 min-h-screen bg-gray-50">
      <ToastContainer position="top-right" autoClose={3000} newestOnTop />

      {/* ── Page Header ── */}
      <div className="mb-4 flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-xl font-bold text-gray-800">Settlement List</h1>
          <p className="text-sm text-gray-500">Provider Payment &rsaquo; Settlement</p>
        </div>

        {/* Batch toolbar buttons */}
        <div className="flex flex-wrap gap-2">
          <button onClick={() => navigate("/settlement-batch-upload")}
            className="flex items-center gap-1 px-3 py-1.5 text-sm bg-sky-600 text-white rounded hover:bg-sky-700">
            <FontAwesomeIcon icon={faUpload} /> Settlement Batch Upload
          </button>
          <button onClick={() => setCheckStatusReportModal(true)}
            className="flex items-center gap-1 px-3 py-1.5 text-sm bg-cyan-700 text-white rounded hover:bg-cyan-800">
            <FontAwesomeIcon icon={faDownload} /> Check Status Report
          </button>
          <button onClick={() => navigate("/check-status-batch-upload")}
            className="flex items-center gap-1 px-3 py-1.5 text-sm bg-violet-600 text-white rounded hover:bg-violet-700">
            <FontAwesomeIcon icon={faFileUpload} /> Batch Check Status Upload
          </button>
          <button onClick={() => navigate("/depo-or-batch-upload")}
            className="flex items-center gap-1 px-3 py-1.5 text-sm bg-teal-600 text-white rounded hover:bg-teal-700">
            <FontAwesomeIcon icon={faFileUpload} /> Deposit Slip/OR Upload
          </button>
          <button onClick={() => navigate("/deposit-slip-batch-upload")}
            className="flex items-center gap-1 px-3 py-1.5 text-sm bg-indigo-600 text-white rounded hover:bg-indigo-700">
            <FontAwesomeIcon icon={faFileUpload} /> Batch Deposit Slip Upload
          </button>
          <div className="relative">
            <span className="absolute -top-2 -left-2 z-10 rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide bg-gray-500 text-gray-100">
              Deprecated
            </span>
            <button
              disabled
              onClick={() => setBatchDeductedModal(true)}
              className="flex items-center gap-1 px-3 py-1.5 text-sm bg-gray-400 text-gray-100 rounded cursor-not-allowed opacity-70"
              title="Deprecated"
            >
              <FontAwesomeIcon icon={faCalendarAlt} /> Batch Date Deducted
            </button>
          </div>
          <button onClick={() => setDeliveryLabelModal(true)}
            className="flex items-center gap-1 px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700">
            <FontAwesomeIcon icon={faFileAlt} /> Delivery Address Generation
          </button>
          <button onClick={() => setBatchPrintModal(true)}
            className="flex items-center gap-1 px-3 py-1.5 text-sm bg-orange-600 text-white rounded hover:bg-orange-700">
            <FontAwesomeIcon icon={faPrint} /> Batch Print
          </button>
          {selectedTblIds.length > 0 && (
            <button
              onClick={handleGenerateSelectedCheckVouchers}
              disabled={submitting}
              className={`flex items-center gap-1 px-3 py-1.5 text-sm bg-green-600 text-white rounded hover:bg-green-700 ${submitting ? "opacity-50 cursor-not-allowed" : ""}`}
            >
              <FontAwesomeIcon icon={faPrint} /> Generate CV ({selectedTblIds.length})
            </button>
          )}
          <button onClick={handleExportCsv}
            className="flex items-center gap-1 px-3 py-1.5 text-sm bg-gray-600 text-white rounded hover:bg-gray-700">
            <FontAwesomeIcon icon={faDownload} /> CSV
          </button>
        </div>
      </div>

      {/* ── Search Bar ── */}
      <div className="mb-4 flex flex-wrap gap-2 items-center">
        <select value={searchBy} onChange={(e) => { setSearchBy(e.target.value); setPage(1); }}
          className="border rounded px-2 py-1.5 text-sm bg-white">
          <option value="all">All Fields</option>
          <option value="check_no">Check No.</option>
          <option value="cv_no">CV#</option>
          <option value="provider_name">Provider</option>
          <option value="payment_ref">Payment Ref #</option>
        </select>
        <div className="flex">
          <input value={search} onChange={(e) => setSearch(e.target.value)}
            onKeyDown={handleSearchKeyDown}
            placeholder="Search..."
            className="border rounded-l px-3 py-1.5 text-sm w-64 focus:outline-none focus:ring-1 focus:ring-blue-400" />
          <button onClick={handleSearch}
            className="px-3 py-1.5 bg-blue-600 text-white rounded-r hover:bg-blue-700">
            <FontAwesomeIcon icon={faSearch} />
          </button>
        </div>
        <select value={perPage} onChange={(e) => { setPerPage(Number(e.target.value)); setPage(1); }}
          className="border rounded px-2 py-1.5 text-sm bg-white">
          {[10, 25, 50, 100].map((n) => <option key={n} value={n}>{n} / page</option>)}
        </select>
        <button onClick={fetchData} className="flex items-center gap-1 px-3 py-1.5 text-sm bg-white border rounded hover:bg-gray-100">
          <FontAwesomeIcon icon={faSyncAlt} /> Refresh
        </button>
        {activeColumnFilterCount > 0 && (
          <button
            onClick={clearColumnFilters}
            className="flex items-center gap-1 px-3 py-1.5 text-sm bg-white border border-red-200 text-red-600 rounded hover:bg-red-50"
          >
            <FontAwesomeIcon icon={faTimes} /> Clear filters ({activeColumnFilterCount})
          </button>
        )}
        {selectedTblIds.length > 0
          ? <button onClick={clearAll} className="text-sm text-red-600 underline">Clear selection ({selectedTblIds.length})</button>
          : <button onClick={selectAll} className="text-sm text-blue-600 underline">Select all visible</button>
        }
      </div>

      {/* ── Table ── */}
      <div className="overflow-x-auto rounded-xl shadow bg-white">
        <table className="min-w-full text-xs divide-y divide-gray-200">
          <thead className="bg-gray-100 sticky top-0 z-10">
            <tr>
              {/* Actions */}
              <th className="px-2 py-2 text-left font-semibold text-gray-600 whitespace-nowrap">Actions</th>
              {SETTLEMENT_TABLE_COLUMNS.map(({ col, label }) => (
                <th key={col}
                  className="px-2 py-2 text-left font-semibold text-gray-600 whitespace-nowrap select-none">
                  <button
                    type="button"
                    onClick={() => handleSort(col)}
                    className="inline-flex items-center text-left hover:text-blue-700"
                  >
                    {label}<SortIcon col={col} sortBy={sortBy} sortDir={sortDir} />
                  </button>
                </th>
              ))}
            </tr>
            <tr className="bg-gray-50">
              <th className="px-2 py-1 text-left font-semibold text-gray-500 whitespace-nowrap"></th>
              {SETTLEMENT_TABLE_COLUMNS.map(({ col, filterKey, filterClassName }) => (
                <th key={`${col}-filter`} className="px-2 py-1 align-top">
                  {filterKey ? (
                    <ColumnFilterSelect
                      options={normalizeColumnFilterOptions(filterKey, filterOptions[filterKey])}
                      value={columnFilters[filterKey] ?? []}
                      onChange={(values) => handleColumnFilterChange(filterKey, values)}
                      className={filterClassName}
                    />
                  ) : null}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {loading && (
              <tr><td colSpan={SETTLEMENT_TABLE_COLUMNS.length + 1} className="text-center py-8 text-gray-400">Loading...</td></tr>
            )}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={SETTLEMENT_TABLE_COLUMNS.length + 1} className="text-center py-8 text-gray-400">No records found.</td></tr>
            )}
            {!loading && rows.map((row) => {
              const isSelected = selectedTblIds.includes(row.tbl_id);

              const pisV1Url = buildLegacyDownloadUrl("pis", row.pis_file);
              const pisV2Url = buildS3PublicShareDownloadUrl(row.pis_public_share_url);
              const pisNoPpdV1Url = buildLegacyDownloadUrl("pis", row.pis_no_ppd_file);
              const pisNoPpdV2Url = buildS3PublicShareDownloadUrl(row.pis_no_ppd_public_share_url);
              const form2307V1Url = buildLegacyDownloadUrl("form-2307", row.form_2307_file);
              const form2307V2Url = buildS3PublicShareDownloadUrl(row.form_2307_public_share_url);
              const billingUrl   = row.billing_file   ? `/storage/settlement_billing/${row.billing_file}` : null;
              const depositSlipFile = String(row.deposit_slip_file ?? "").trim();
              const depositSlipHasLegacyMeta = depositSlipFile.includes("_|");
              const depositSlipV1Url = buildLegacyDepositSlipDownloadUrl(depositSlipFile);
              const depositSlipV2Url = buildS3PublicShareDownloadUrl(row.deposit_slip_public_share_url);
              const orUrl        = row.or_public_share_url
                || (/^https?:\/\//i.test(row.or_file || "") ? row.or_file : null)
                || (row.or_file ? `/storage/settlement_attachments/${row.or_file}` : null);
              const providerOrV1Url = buildLegacyDownloadUrl("settlement_provider_or", row.provider_or_file);
              const providerOrV2Url = buildS3PublicShareDownloadUrl(row.provider_or_public_share_url);

              return (
                <tr key={row.tbl_id}
                  className={`hover:bg-sky-50 transition-colors ${isSelected ? "bg-sky-100" : ""}`}>

                  {/* ── Actions column ── */}
                  <td className="px-2 py-1.5 whitespace-nowrap">
                    <div className="flex flex-col gap-1">
                      <div className="flex gap-1">
                        {/* Tag Date Deducted */}
                        <button title="Tag Actual Date Deducted"
                          onClick={() => openDeductedModal(row)}
                          className="p-1 rounded bg-purple-100 text-purple-700 hover:bg-purple-200">
                          <FontAwesomeIcon icon={faCalendarAlt} />
                        </button>
                        {/* Insert Cheque Number */}
                        <button title="Insert Cheque Number"
                          onClick={() => openChequeModal(row)}
                          className="p-1 rounded bg-green-100 text-green-700 hover:bg-green-200">
                          <FontAwesomeIcon icon={faCog} />
                        </button>
                        {/* Tag Check Status */}
                        <button title="Tag Check Status"
                          onClick={() => openCheckStatusModal(row)}
                          className="p-1 rounded bg-yellow-100 text-yellow-700 hover:bg-yellow-200">
                          <FontAwesomeIcon icon={faTags} />
                        </button>
                      </div>
                      <div className="flex gap-1">
                        {/* View Check Voucher */}
                        <button title="View Check Voucher"
                          onClick={() => handleViewCheckVoucher(row)}
                          disabled={submitting}
                          className={`p-1 rounded bg-blue-100 text-blue-700 hover:bg-blue-200 ${submitting ? "opacity-50 cursor-not-allowed" : ""}`}>
                          <FontAwesomeIcon icon={faFileAlt} />
                        </button>
                        {/* Select for batch CV */}
                        <button title={isSelected ? "Deselect" : "Select for Batch CV"}
                          onClick={() => toggleSelect(row.tbl_id)}
                          className={`p-1 rounded hover:bg-blue-200 ${isSelected ? "bg-blue-500 text-white" : "bg-blue-100 text-blue-700"}`}>
                          <FontAwesomeIcon icon={faCheckSquare} />
                        </button>
                      </div>
                    </div>
                  </td>

                  {/* PPD Term */}
                  <td className="px-2 py-1.5 whitespace-nowrap"><PpdTermBadge value={row.ppd_term} /></td>
                  {/* Payment Mode */}
                  <td className="px-2 py-1.5 whitespace-nowrap">{row.payment_mode || ""}</td>
                  {/* Date Processed */}
                  <td className="px-2 py-1.5 whitespace-nowrap">{row.processed_date || ""}</td>
                  {/* Due Date */}
                  <td className="px-2 py-1.5 whitespace-nowrap">{row.due_date || ""}</td>
                  {/* Actual Date Deducted */}
                  <td className="px-2 py-1.5 whitespace-nowrap">{row.date_deducted || ""}</td>
                  {/* TAT */}
                  <td className="px-2 py-1.5 whitespace-nowrap">{row.tat ?? ""}</td>
                  {/* Provider */}
                  <td className="px-2 py-1.5 whitespace-nowrap max-w-[180px] truncate" title={row.provider_name}>{row.provider_name || ""}</td>
                  {/* Bank Charge */}
                  <td className="px-2 py-1.5 whitespace-nowrap text-right">{row.bank_charge != null ? formatCurrency(row.bank_charge) : ""}</td>
                  {/* Net Payable */}
                  <td className="px-2 py-1.5 whitespace-nowrap text-right font-medium">{row.net_payable != null ? formatCurrency(row.net_payable) : ""}</td>
                  {/* Check Number */}
                  <td className="px-2 py-1.5 whitespace-nowrap">{row.check_no || ""}</td>
                  {/* CV# */}
                  <td className="px-2 py-1.5 whitespace-nowrap">{row.cv_no || ""}</td>
                  {/* Date Credited */}
                  <td className="px-2 py-1.5 whitespace-nowrap">{toDisplayDate(row.credited_date) || ""}</td>
                  {/* Payment Ref # */}
                  <td className="px-2 py-1.5 whitespace-nowrap">{row.payment_ref_no || ""}</td>

                  {/* ── PIS ── */}
                  <td className="px-2 py-1.5 whitespace-nowrap">
                    {row.pis_tag === 1 ? (
                      <FileDownloadOptions
                        v1Url={pisV1Url}
                        v2Url={pisV2Url}
                        statusOk={row.sent_mail_tag === 1 || row.sent_mail_tag == null}
                      />
                    ) : (
                      <button onClick={() => openAttachModal(row, "deposit_slip")}
                        className="inline-flex items-center gap-1 px-2 py-0.5 text-xs bg-gray-100 text-gray-600 rounded hover:bg-gray-200">
                        <FontAwesomeIcon icon={faPaperclip} /> Attach
                      </button>
                    )}
                  </td>

                  {/* ── PIS (No PPD) ── */}
                  <td className="px-2 py-1.5 whitespace-nowrap">
                    {row.pis_no_ppd_file ? (
                      <FileDownloadOptions
                        v1Url={pisNoPpdV1Url}
                        v2Url={pisNoPpdV2Url}
                        statusOk={row.sent_mail_tag === 1 || row.sent_mail_tag == null}
                      />
                    ) : (
                      <span className="text-gray-400 text-xs">No File</span>
                    )}
                  </td>

                  {/* ── Form 2307 ── */}
                  <td className="px-2 py-1.5 whitespace-nowrap">
                    {row.form_2307_tag === 1 ? (
                      row.form_2307_file ? (
                        <div className="flex flex-col gap-1">
                          <FileDownloadOptions
                            v1Url={form2307V1Url}
                            v2Url={form2307V2Url}
                            statusOk={row.sent_mail_tag === 1 || row.sent_mail_tag == null}
                          />
                          <button onClick={() => openForm2307Modal(row)}
                            className="inline-flex items-center gap-1 px-2 py-0.5 text-xs bg-gray-100 text-gray-600 rounded hover:bg-gray-200">
                            <FontAwesomeIcon icon={faPaperclip} /> Change
                          </button>
                        </div>
                      ) : (
                        <span className="text-xs text-gray-500">With Not Withheld</span>
                      )
                    ) : (
                      <button onClick={() => openForm2307Modal(row)}
                        className="inline-flex items-center gap-1 px-2 py-0.5 text-xs bg-gray-100 text-gray-600 rounded hover:bg-gray-200">
                        <FontAwesomeIcon icon={faPaperclip} /> Attach
                      </button>
                    )}
                  </td>

                  {/* ── Billing PPD ── */}
                  <td className="px-2 py-1.5 whitespace-nowrap">
                    {row.billing_tag === 1 && row.billing_file ? (
                      <a href={billingUrl} target="_blank" rel="noreferrer" download
                        className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded ${row.sent_mail_tag === 1 || row.sent_mail_tag == null ? "bg-green-100 text-green-700" : "bg-red-100 text-red-600"}`}>
                        <FontAwesomeIcon icon={faDownload} /> Attached
                      </a>
                    ) : row.billing_tag === 1 ? (
                      <span className="text-xs text-gray-500">No PPD</span>
                    ) : row.billing_tag === 2 ? (
                      <span className="text-xs text-gray-500">{row.payment_ref_no?.startsWith("D-") ? "Not Provider" : "Not Outright"}</span>
                    ) : row.billing_tag === 4 ? (
                      <span className="text-xs text-gray-500">Not Required</span>
                    ) : (
                      <button onClick={() => openAttachModal(row, "billing_ppd")}
                        className="inline-flex items-center gap-1 px-2 py-0.5 text-xs bg-gray-100 text-gray-600 rounded hover:bg-gray-200">
                        <FontAwesomeIcon icon={faPaperclip} /> Attach
                      </button>
                    )}
                  </td>

                  {/* ── Deposit Slip ── */}
                  <td className="px-2 py-1.5 whitespace-nowrap">
                    {row.deposit_slip_tag === 1 ? (
                      <div className="flex items-start gap-1">
                        {depositSlipV1Url || depositSlipV2Url ? (
                          <FileDownloadOptions
                            v1Url={depositSlipV1Url}
                            v2Url={depositSlipV2Url}
                            statusOk={row.sent_mail_tag === 1 || row.sent_mail_tag == null}
                          />
                        ) : (
                          <span className="inline-flex items-center px-2 py-0.5 text-xs rounded bg-yellow-100 text-yellow-700">
                            {depositSlipHasLegacyMeta ? "Legacy DS" : "No Attachment"}
                          </span>
                        )}
                        <button onClick={() => openAttachModal(row, "deposit_slip")}
                          className="inline-flex items-center gap-1 px-2 py-0.5 text-xs bg-blue-100 text-blue-700 rounded hover:bg-blue-200">
                          Change
                        </button>
                      </div>
                    ) : (
                      <button onClick={() => openAttachModal(row, "deposit_slip")}
                        className="inline-flex items-center gap-1 px-2 py-0.5 text-xs bg-gray-100 text-gray-600 rounded hover:bg-gray-200">
                        <FontAwesomeIcon icon={faPaperclip} /> Attach
                      </button>
                    )}
                  </td>

                  {/* Mail Sent */}
                  <td className="px-2 py-1.5 whitespace-nowrap">{row.pis_mail_datetime || ""}</td>

                  {/* ── Amaphil OR ── */}
                  <td className="px-2 py-1.5 whitespace-nowrap">
                    {row.or_tag === 1 && (row.or_file || row.or_public_share_url) ? (
                      <div className="flex gap-1">
                        <a href={orUrl} target="_blank" rel="noreferrer" download
                          className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded ${row.sent_mail_tag === 1 || row.sent_mail_tag == null ? "bg-green-100 text-green-700" : "bg-red-100 text-red-600"}`}>
                          <FontAwesomeIcon icon={faDownload} /> Attached
                        </a>
                        <button onClick={() => openAttachModal(row, "or_amap")}
                          className="inline-flex items-center gap-1 px-2 py-0.5 text-xs bg-blue-100 text-blue-700 rounded hover:bg-blue-200">
                          View/Edit
                        </button>
                      </div>
                    ) : (
                      <button onClick={() => openAttachModal(row, "or_amap")}
                        className="inline-flex items-center gap-1 px-2 py-0.5 text-xs bg-gray-100 text-gray-600 rounded hover:bg-gray-200">
                        <FontAwesomeIcon icon={faPaperclip} /> Attach
                      </button>
                    )}
                  </td>

                  {/* ── Provider OR ── */}
                  <td className="px-2 py-1.5 whitespace-nowrap">
                    {row.provider_or_tag === 1 && (row.provider_or_file || row.provider_or_public_share_url) ? (
                      <div className="flex items-start gap-1">
                        <FileDownloadOptions
                          v1Url={providerOrV1Url}
                          v2Url={providerOrV2Url}
                          statusOk={row.sent_mail_tag === 1 || row.sent_mail_tag == null}
                        />
                        <button onClick={() => openProvOrModal(row)}
                          className="inline-flex items-center gap-1 px-2 py-0.5 text-xs bg-blue-100 text-blue-700 rounded hover:bg-blue-200">
                          Change
                        </button>
                      </div>
                    ) : (
                      <button onClick={() => openProvOrModal(row)}
                        className="inline-flex items-center gap-1 px-2 py-0.5 text-xs bg-gray-100 text-gray-600 rounded hover:bg-gray-200">
                        <FontAwesomeIcon icon={faPaperclip} /> Attach
                      </button>
                    )}
                  </td>

                  {/* Provider OR No. */}
                  <td className="px-2 py-1.5 whitespace-nowrap">{row.provider_or_no || ""}</td>
                  {/* Status */}
                  <td className="px-2 py-1.5 whitespace-nowrap"><StatusBadge value={row.status} /></td>
                  {/* PPD */}
                  <td className="px-2 py-1.5 whitespace-nowrap text-right">{row.ppd != null ? formatCurrency(row.ppd) : ""}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* ── Pagination ── */}
      <div className="mt-4 flex items-center justify-between text-sm text-gray-600 flex-wrap gap-2">
        <span>
          {pagination.from ?? 0}–{pagination.to ?? 0} of {pagination.total ?? 0} records
        </span>
        <div className="flex items-center gap-1">
          <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1}
            className="px-2 py-1 border rounded disabled:opacity-50 hover:bg-gray-100">
            <FontAwesomeIcon icon={faAngleLeft} />
          </button>
          <span className="px-3">Page {page} / {totalPages}</span>
          <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages}
            className="px-2 py-1 border rounded disabled:opacity-50 hover:bg-gray-100">
            <FontAwesomeIcon icon={faAngleRight} />
          </button>
        </div>
      </div>

      {/* ══════════════════════════════════════════════════════════════════════ */}
      {deliveryLabelModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-3">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-7xl max-h-[92vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between px-4 py-3 bg-sky-600 text-white">
              <h2 className="font-semibold text-sm">Provider Delivery Address Label Generation</h2>
              <div className="flex items-center gap-2">
                <button
                  onClick={handleOpenDeliveryLabelFullPage}
                  className="relative px-2.5 py-1 text-xs font-semibold bg-white text-sky-700 rounded hover:bg-sky-50"
                >
                  View In Full Page
                  <span className="absolute -top-1.5 -right-1.5 bg-red-600 text-white text-[10px] leading-none px-1 py-0.5 rounded-full">
                    New
                  </span>
                </button>
                <button
                  onClick={() => setDeliveryLabelModal(false)}
                  className="text-white hover:text-gray-200"
                >
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              </div>
            </div>

            <ProviderDeliveryAddressLabelGenerator
              initialProviderCodes={deliveryLabelSnapshot.providerCodes}
              initialRows={deliveryLabelSnapshot.rows}
              initialStatus={deliveryLabelSnapshot.status}
              initialFromContactNumber={deliveryLabelSnapshot.fromContactNumber}
              onStateChange={setDeliveryLabelSnapshot}
              rootClassName="flex-1 flex flex-col min-h-0"
              previewClassName="flex-1 overflow-y-auto p-4 bg-white"
            />

            <div className="px-4 py-3 border-t bg-gray-50 flex justify-end">
              <button
                onClick={() => setDeliveryLabelModal(false)}
                className="px-4 py-1.5 text-sm border rounded hover:bg-gray-100"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal: Insert Cheque Number ── */}
      {/* ══════════════════════════════════════════════════════════════════════ */}
      {chequeModal && (
        <ModalWrapper title="Insert / Edit Cheque Number" onClose={() => setChequeModal(false)}>
          <p className="text-xs text-gray-500 mb-3">
            PRN: <strong>{chequeData.payment_ref_no}</strong> | Provider: <strong>{chequeData.payee_name}</strong>
          </p>
          <div className="grid grid-cols-2 gap-3">
            <LabelInput label="Check No. *" value={chequeData.check_no} onChange={(v) => setChequeData((p) => ({ ...p, check_no: v }))} />
            <LabelInput label="Check Amount" type="number" value={chequeData.check_amount} onChange={(v) => setChequeData((p) => ({ ...p, check_amount: v }))} />
            <LabelInput label="CV Date (DD/MM/YYYY)" value={chequeData.credited_date} placeholder="DD/MM/YYYY" onChange={(v) => setChequeData((p) => ({ ...p, credited_date: v }))} />
            <LabelInput label="Provider OR No." value={chequeData.provider_or_no} onChange={(v) => setChequeData((p) => ({ ...p, provider_or_no: v }))} />
            <LabelInput label="Provider OR Date" type="date" value={chequeData.provider_or_date} onChange={(v) => setChequeData((p) => ({ ...p, provider_or_date: v }))} />
            <LabelInput label="Provider OR Amount" type="number" value={chequeData.provider_or_amount} onChange={(v) => setChequeData((p) => ({ ...p, provider_or_amount: v }))} />
          </div>
          <ModalFooter onCancel={() => setChequeModal(false)} onSave={handleSaveCheque} submitting={submitting} />
        </ModalWrapper>
      )}

      {/* ══════════════════════════════════════════════════════════════════════ */}
      {/* ── Modal: Attach Files (Deposit Slip / Amaphil OR) ── */}
      {/* ══════════════════════════════════════════════════════════════════════ */}
      {attachModal && (
        <ModalWrapper
          title={
            attachData.type === "or_amap"
              ? "Attach / Edit Amaphil O.R"
              : attachData.type === "billing_ppd"
                ? "Attach Billing PPD"
                : "Upload / Change Deposit Slip"
          }
          onClose={() => setAttachModal(false)}>
          <p className="text-xs text-gray-500 mb-3">
            PRN: <strong>{attachData.payment_ref_no || "-"}</strong> | Provider: <strong>{attachData.provider_name || "-"}</strong>
            {" "} | Check No: <strong>{attachData.check_no || "-"}</strong> | CV Date: <strong>{attachData.credited_date || "-"}</strong>
          </p>
          {attachData.type === "deposit_slip" && attachData.has_existing && (
            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 mb-3">
              Existing Deposit Slip will be replaced once you submit.
            </p>
          )}
          {attachData.type === "or_amap" && attachData.has_existing && (
            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 mb-3">
              Existing Amaphil O.R will be replaced once you submit.
            </p>
          )}
          {attachData.type === "billing_ppd" && attachData.has_existing && (
            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 mb-3">
              Existing Billing PPD attachment will be replaced once you submit.
            </p>
          )}
          {(attachData.type === "or_amap" || attachData.type === "billing_ppd") && (
            <div className="grid grid-cols-2 gap-3 mb-3">
              <LabelInput label="Amaphil O.R No. *" value={attachData.amap_or_no} onChange={(v) => setAttachData((p) => ({ ...p, amap_or_no: v }))} />
              <LabelInput label="Amaphil O.R Date *" type="date" value={attachData.amap_or_date} onChange={(v) => setAttachData((p) => ({ ...p, amap_or_date: v }))} />
              <LabelInput label="Amaphil O.R Amount *" type="number" value={attachData.amap_or_amount} onChange={(v) => setAttachData((p) => ({ ...p, amap_or_amount: v }))} />
            </div>
          )}
          {(attachData.type === "deposit_slip" || attachData.type === "billing_ppd") && (
            <label className="inline-flex items-center gap-2 mb-3 text-xs text-gray-600">
              <input
                type="checkbox"
                checked={Boolean(attachData.no_attachment)}
                onChange={(e) => {
                  const checked = e.target.checked;
                  setAttachData((p) => ({
                    ...p,
                    no_attachment: checked,
                    file: checked ? null : p.file,
                  }));
                }}
                className="rounded border-gray-300"
              />
              {attachData.type === "billing_ppd"
                ? "No Attachment"
                : "No Attachment (marks Deposit Slip as attached without file, same as v1 behavior)"}
            </label>
          )}
          <div className="mb-3">
            <label className="block text-xs font-medium text-gray-600 mb-1">
              {(attachData.type === "or_amap" || attachData.type === "billing_ppd")
                ? "Attachment SOA *"
                : "File (pdf/jpg/jpeg/png)"}
            </label>
            <input type="file" accept=".pdf,.jpg,.jpeg,.png"
              disabled={(attachData.type === "deposit_slip" || attachData.type === "billing_ppd") && attachData.no_attachment}
              onChange={(e) => {
                const nextFile = e.target.files[0] || null;
                setAttachData((p) => ({
                  ...p,
                  file: nextFile,
                  no_attachment: nextFile ? false : p.no_attachment,
                }));
              }}
              className="block w-full text-sm text-gray-600 file:mr-3 file:py-1 file:px-3 file:rounded file:border-0 file:text-xs file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100" />
          </div>
          <ModalFooter onCancel={() => setAttachModal(false)} onSave={handleSaveAttachment} submitting={submitting} />
        </ModalWrapper>
      )}

      {/* ══════════════════════════════════════════════════════════════════════ */}
      {/* ── Modal: Form 2307 ── */}
      {/* ══════════════════════════════════════════════════════════════════════ */}
      {form2307Modal && (
        <ModalWrapper title="Upload / Change Form 2307" onClose={() => setForm2307Modal(false)}>
          {Number(form2307Row?.form_2307_tag || 0) === 1 && !!(form2307Row?.form_2307_file || form2307Row?.form_2307_public_share_url) && (
            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 mb-3">
              Existing Form 2307 will be replaced once you submit a new file.
            </p>
          )}
          <div className="mb-3">
            <label className="block text-xs font-medium text-gray-600 mb-1">File (pdf/jpg/jpeg/png) *</label>
            <input type="file" accept=".pdf,.jpg,.jpeg,.png"
              onChange={(e) => setForm2307File(e.target.files[0] || null)}
              className="block w-full text-sm text-gray-600 file:mr-3 file:py-1 file:px-3 file:rounded file:border-0 file:text-xs file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100" />
          </div>
          <ModalFooter onCancel={() => setForm2307Modal(false)} onSave={handleSaveForm2307} submitting={submitting} />
        </ModalWrapper>
      )}

      {/* ══════════════════════════════════════════════════════════════════════ */}
      {/* ── Modal: Provider OR ── */}
      {/* ══════════════════════════════════════════════════════════════════════ */}
      {provOrModal && (
        <ModalWrapper title="Upload Provider OR" onClose={() => setProvOrModal(false)}>
          {Number(provOrRow?.provider_or_tag || 0) === 1 && !!(provOrRow?.provider_or_file || provOrRow?.provider_or_public_share_url) && (
            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 mb-3">
              Existing Provider OR will be replaced once you submit a new file.
            </p>
          )}
          <div className="mb-3">
            <label className="block text-xs font-medium text-gray-600 mb-1">File (pdf/jpg/jpeg/png) *</label>
            <input type="file" accept=".pdf,.jpg,.jpeg,.png"
              onChange={(e) => setProvOrFile(e.target.files[0] || null)}
              className="block w-full text-sm text-gray-600 file:mr-3 file:py-1 file:px-3 file:rounded file:border-0 file:text-xs file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100" />
          </div>
          <ModalFooter onCancel={() => setProvOrModal(false)} onSave={handleSaveProvOr} submitting={submitting} />
        </ModalWrapper>
      )}

      {/* ══════════════════════════════════════════════════════════════════════ */}
      {/* ── Modal: Tag Date Deducted ── */}
      {/* ══════════════════════════════════════════════════════════════════════ */}
      {deductedModal && (
        <ModalWrapper title="Tag Actual Date Deducted" onClose={() => setDeductedModal(false)}>
          <p className="text-xs text-gray-500 mb-3">
            PRN: <strong>{deductedRow?.payment_ref_no}</strong>
          </p>
          <LabelInput label="Actual Date Deducted *" type="date" value={dateDeducted}
            onChange={(v) => setDateDeducted(v)} />
          <ModalFooter onCancel={() => setDeductedModal(false)} onSave={handleTagDateDeducted} submitting={submitting} />
        </ModalWrapper>
      )}

      {/* ══════════════════════════════════════════════════════════════════════ */}
      {/* ── Modal: Batch Printing of PIS and Form2307 ── */}
      {/* ══════════════════════════════════════════════════════════════════════ */}
      {/* Modal: Check Status Tagging */}
      {checkStatusReportModal && (
        <ModalWrapper title="Check Status Report Filters" onClose={() => setCheckStatusReportModal(false)} maxWidth="max-w-2xl">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <LabelInput
              label="Check Date From"
              type="date"
              value={checkStatusReportFilters.date_from}
              onChange={(v) => setCheckStatusReportFilters((prev) => ({ ...prev, date_from: v }))}
            />
            <LabelInput
              label="Check Date To"
              type="date"
              value={checkStatusReportFilters.date_to}
              onChange={(v) => setCheckStatusReportFilters((prev) => ({ ...prev, date_to: v }))}
            />
            <LabelInput
              label="New Check Date From"
              type="date"
              value={checkStatusReportFilters.new_date_from}
              onChange={(v) => setCheckStatusReportFilters((prev) => ({ ...prev, new_date_from: v }))}
            />
            <LabelInput
              label="New Check Date To"
              type="date"
              value={checkStatusReportFilters.new_date_to}
              onChange={(v) => setCheckStatusReportFilters((prev) => ({ ...prev, new_date_to: v }))}
            />
          </div>
          <div className="mt-4 flex justify-between gap-2">
            <button
              type="button"
              onClick={() => setCheckStatusReportFilters({ date_from: "", date_to: "", new_date_from: "", new_date_to: "" })}
              className="px-3 py-1.5 rounded bg-gray-100 text-gray-700 hover:bg-gray-200 text-sm"
              disabled={submitting}
            >
              Clear Filters
            </button>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setCheckStatusReportModal(false)}
                className="px-3 py-1.5 rounded bg-gray-200 text-gray-700 hover:bg-gray-300 text-sm"
                disabled={submitting}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleExportCheckStatusReport}
                className="px-3 py-1.5 rounded bg-green-600 text-white hover:bg-green-700 text-sm disabled:bg-gray-400"
                disabled={submitting}
              >
                {submitting ? "Generating..." : "Generate CSV"}
              </button>
            </div>
          </div>
        </ModalWrapper>
      )}

      {checkStatusModal && (
        <ModalWrapper title="Check Status Tagging" onClose={() => setCheckStatusModal(false)} maxWidth="max-w-3xl">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <ReadOnlyField label="CV No." value={checkStatusData.cv_no} />
            <ReadOnlyField label="Check Number" value={checkStatusData.check_no} />
            <ReadOnlyField label="Payment Reference #" value={checkStatusData.payment_ref_no} />
            <ReadOnlyField label="Provider" value={checkStatusData.provider_name} />
            <LabelInput
              label="Check Date *"
              type="date"
              value={checkStatusData.check_date}
              onChange={(v) => updateCheckStatusData("check_date", v)}
            />
            <ReadOnlyField label="Computed Stale Date" value={checkStatusData.computed_stale_date} />
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Check Status *</label>
              <select
                value={checkStatusData.check_status}
                onChange={(e) => updateCheckStatusData("check_status", e.target.value)}
                className="w-full border rounded px-2 py-1.5 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-blue-400"
              >
                <option value="">Select Status</option>
                {CHECK_STATUS_OPTIONS.map((status) => (
                  <option key={status} value={status}>{status}</option>
                ))}
              </select>
            </div>
            <LabelInput
              label="Date Cleared"
              type="date"
              value={checkStatusData.date_cleared}
              onChange={(v) => updateCheckStatusData("date_cleared", v)}
            />
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Replacement Check Indicator</label>
              <select
                value={checkStatusData.replacement_check_indicator}
                onChange={(e) => updateCheckStatusData("replacement_check_indicator", e.target.value)}
                className="w-full border rounded px-2 py-1.5 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-blue-400"
              >
                <option value="0">No</option>
                <option value="1">Yes</option>
              </select>
            </div>
          </div>

          {checkStatusData.replacement_check_indicator === "1" && (
            <div className="mt-4 rounded border border-sky-200 bg-sky-50 p-3">
              <div className="mb-3 text-sm font-semibold text-sky-800">Replacement Check Details</div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <LabelInput
                  label="Original Check Number *"
                  value={checkStatusData.original_check_number}
                  onChange={(v) => updateCheckStatusData("original_check_number", v)}
                />
                <LabelInput
                  label="New Check Number *"
                  value={checkStatusData.new_check_number}
                  onChange={(v) => updateCheckStatusData("new_check_number", v)}
                />
                <LabelInput
                  label="New Check Date *"
                  type="date"
                  value={checkStatusData.new_check_date}
                  onChange={(v) => updateCheckStatusData("new_check_date", v)}
                />
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Reason for Replacement *</label>
                  <select
                    value={checkStatusData.reason_for_replacement}
                    onChange={(e) => updateCheckStatusData("reason_for_replacement", e.target.value)}
                    className="w-full border rounded px-2 py-1.5 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-blue-400"
                  >
                    <option value="">Select Reason</option>
                    {REPLACEMENT_REASON_OPTIONS.map((reason) => (
                      <option key={reason} value={reason}>{reason}</option>
                    ))}
                  </select>
                </div>
                <LabelInput
                  label="Replacement Fee Deduction Amount"
                  type="number"
                  value={checkStatusData.replacement_fee_deduction_amount}
                  onChange={(v) => updateCheckStatusData("replacement_fee_deduction_amount", v)}
                />
                <ReadOnlyField label="Number of Times Replaced" value={checkStatusData.number_of_times_replaced} />
              </div>
              {Number(checkStatusData.needs_replacement_letter || 0) === 1 && (
                <p className="mt-3 rounded border border-red-200 bg-red-50 px-2 py-1 text-xs text-red-700">
                  This check has been replaced more than once. Issue a Check Replacement Letter.
                </p>
              )}
            </div>
          )}

          <div className="mt-3">
            <label className="block text-xs font-medium text-gray-600 mb-1">Remarks</label>
            <textarea
              value={checkStatusData.remarks}
              onChange={(e) => updateCheckStatusData("remarks", e.target.value)}
              rows={2}
              className="w-full border rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400"
            />
          </div>

          <ModalFooter onCancel={() => setCheckStatusModal(false)} onSave={handleSaveCheckStatus} submitting={submitting} />
        </ModalWrapper>
      )}

      {batchPrintModal && (
        <ModalWrapper title="Batch Printing of PIS and Form2307" onClose={() => setBatchPrintModal(false)}>
          <div className="mb-3 flex justify-end">
            <button
              onClick={handleOpenBatchPrintFullPage}
              className="relative inline-flex items-center gap-1 rounded border border-sky-200 bg-white px-3 py-1.5 text-sm font-medium text-sky-700 hover:bg-sky-50"
            >
              Vew in full page
              <span className="absolute -top-2 -right-2 rounded bg-red-700 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white">
                New
              </span>
            </button>
          </div>
          <BatchPrintPisAndForm2307Panel
            onClose={() => setBatchPrintModal(false)}
            autoCloseOnSuccess
          />
        </ModalWrapper>
      )}

      {/* ══════════════════════════════════════════════════════════════════════ */}
      {/* ══════════════════════════════════════════════════════════════════════ */}
      {/* ══════════════════════════════════════════════════════════════════════ */}
      {/* ══════════════════════════════════════════════════════════════════════ */}
      {/* ══════════════════════════════════════════════════════════════════════ */}
      {/* ── Modal: Batch Date Deducted CSV ── */}
      {/* ══════════════════════════════════════════════════════════════════════ */}
      {batchDeductedModal && (
        <ModalWrapper title="Batch Date Deducted CSV Upload" onClose={() => setBatchDeductedModal(false)}>
          <div className="mb-3">
            <label className="block text-xs font-medium text-gray-600 mb-1">CSV / TXT File *</label>
            <input type="file" accept=".csv,.txt"
              onChange={(e) => setBatchDeductedFile(e.target.files[0] || null)}
              className="block w-full text-sm text-gray-600 file:mr-3 file:py-1 file:px-3 file:rounded file:border-0 file:text-xs file:bg-purple-50 file:text-purple-700 hover:file:bg-purple-100" />
          </div>
          <ModalFooter onCancel={() => setBatchDeductedModal(false)} onSave={handleUploadBatchDateDeducted}
            saveLabel="Upload" submitting={submitting} />
        </ModalWrapper>
      )}
    </div>
  );
};

// ─── Shared sub-components ────────────────────────────────────────────────────

const ColumnFilterSelect = ({ options, value, onChange, className = "" }) => {
  const optionByValue = new Map(options.map((option) => [String(option.value), option]));
  const selectedOptions = (Array.isArray(value) ? value : [])
    .map((item) => {
      const selectedValue = String(item);
      return optionByValue.get(selectedValue) ?? { value: selectedValue, label: selectedValue };
    })
    .filter((option) => option.value !== "");
  const menuPortalTarget = typeof document !== "undefined" ? document.body : null;

  return (
    <CreatableSelect
      isMulti
      isClearable
      closeMenuOnSelect={false}
      hideSelectedOptions={false}
      options={options}
      value={selectedOptions}
      onChange={(selected) => onChange((selected || []).map((option) => String(option.value).trim()).filter(Boolean))}
      placeholder="All"
      noOptionsMessage={() => "No values"}
      formatCreateLabel={(inputValue) => `Use "${inputValue}"`}
      className={`min-w-[150px] text-left font-normal ${className}`}
      classNamePrefix="settlement-column-filter"
      styles={columnFilterSelectStyles}
      maxMenuHeight={220}
      menuPortalTarget={menuPortalTarget}
      menuPosition="fixed"
    />
  );
};

const FileDownloadOptions = ({ v1Url, v2Url, statusOk = true }) => {
  const legacyClass = statusOk
    ? "bg-green-100 text-green-700 hover:bg-green-200"
    : "bg-red-100 text-red-600 hover:bg-red-200";
  const v2Class = statusOk
    ? "bg-blue-100 text-blue-700 hover:bg-blue-200"
    : "bg-red-100 text-red-600 hover:bg-red-200";
  const baseClass = "inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded whitespace-nowrap";

  return (
    <div className="flex flex-col gap-1">
      {v1Url ? (
        <a href={v1Url} target="_blank" rel="noreferrer" download className={`${baseClass} ${legacyClass}`}>
          <FontAwesomeIcon icon={faDownload} /> Download Version 1.0
        </a>
      ) : (
        <span className="text-gray-400 text-xs">v1 file missing</span>
      )}
      {v2Url ? (
        <a href={v2Url} target="_blank" rel="noreferrer" download className={`${baseClass} ${v2Class}`}>
          <FontAwesomeIcon icon={faDownload} /> Download Version 2.0
        </a>
      ) : (
        <span className="text-gray-400 text-xs">v2 file missing</span>
      )}
    </div>
  );
};

const ModalWrapper = ({ title, onClose, children, maxWidth = "max-w-lg" }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
    <div className={`bg-white rounded-xl shadow-2xl w-full ${maxWidth} mx-4 overflow-hidden`}>
      <div className="flex items-center justify-between px-4 py-3 bg-sky-600 text-white">
        <h2 className="font-semibold text-sm">{title}</h2>
        <button onClick={onClose} className="text-white hover:text-gray-200">
          <FontAwesomeIcon icon={faTimes} />
        </button>
      </div>
      <div className="p-4 max-h-[80vh] overflow-y-auto">
        {children}
      </div>
    </div>
  </div>
);

const ReadOnlyField = ({ label, value }) => (
  <div>
    <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
    <div className="min-h-[34px] w-full rounded border bg-gray-50 px-2 py-1.5 text-sm text-gray-700">
      {value || "-"}
    </div>
  </div>
);

const LabelInput = ({ label, value, onChange, type = "text", placeholder = "" }) => (
  <div>
    <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
    <input type={type} value={value ?? ""} placeholder={placeholder} onChange={(e) => onChange(e.target.value)}
      className="w-full border rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400" />
  </div>
);

const ModalFooter = ({ onCancel, onSave, saveLabel = "Save", submitting }) => (
  <div className="flex justify-end gap-2 mt-4 pt-3 border-t">
    <button onClick={onCancel} disabled={submitting}
      className="px-4 py-1.5 text-sm border rounded hover:bg-gray-100 disabled:opacity-50">
      Cancel
    </button>
    <button onClick={onSave} disabled={submitting}
      className="px-4 py-1.5 text-sm bg-sky-600 text-white rounded hover:bg-sky-700 disabled:opacity-50">
      {submitting ? "Saving..." : saveLabel}
    </button>
  </div>
);

export default SettlementListPage;

